import { ReactElement } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Box } from '@twilio-paste/core/box';
import { Button } from '@twilio-paste/core/button';
import { Text } from '@twilio-paste/core/text';

import { oscLayoutCopy } from '@twiliointernal/e2-commons-shared-definitions';
import { useSegment } from '@twiliointernal/e2-commons-shared-hooks';
import { logger } from '@twiliointernal/e2-commons-shared-libs';

import AiNavButton from './aiNavButton';
import SystemStatusMenuIcon from './systemStatusMenuIcon';
import { constants } from '../definitions';
import { useUserDetails } from '../../../hooks';
import { loginRedirectToOsc, signUpUrl } from '../../../utils';
import { trackingDetails } from '../../../definitions/constants';
import twilioLogoRed from '../../../../assets/twilio-logo-red.svg';

import styles from '../layout.module.css';

const UnauthenticatedHeader = (): ReactElement => {
  const { account, user, isLoading, isLoggedIn } = useUserDetails();
  const { trackEvent } = useSegment({
    accountSid: account?.sid,
    userSid: user?.sid,
    isLoggedIn,
    isLoading,
  });
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleNavigate = (url: string, eventName: string): void => {
    logger.info(
      `${constants.logPrefixes.unauthenticatedHeader}::[${eventName}]`,
    );
    trackEvent(eventName);

    window?.history.pushState({}, window.location.href);
    window.location.replace(url);
  };

  return (
    <Box
      as="header"
      className={styles.header}
      backgroundColor="colorBackgroundBody"
      borderBottomColor="colorBorderWeaker"
      borderBottomWidth="borderWidth10"
      borderBottomStyle="solid"
      flex="1 0 auto"
      gridArea="masthead"
    >
      <Box
        as="div"
        className={styles.headerLeft}
        onClick={(): void => navigate('/')}
      >
        <Box as="img" src={twilioLogoRed} alt="Twilio logo" />
        <Text
          as="h1"
          variant="heading10"
          color="colorTextBrandHighlight"
          fontFamily="fontFamilyText"
          fontSize="fontSize70"
          fontWeight="fontWeightNormal"
          marginBottom="space0"
          paddingTop="space10"
        >
          {oscLayoutCopy.unauthenticatedHeader.title}
        </Text>
      </Box>

      <Box as="div" className={styles.headerRight}>
        <AiNavButton />
        <SystemStatusMenuIcon />
        <Button
          variant="secondary"
          onClick={(): void => {
            handleNavigate(signUpUrl, trackingDetails.signUpToTwilio);
          }}
        >
          {oscLayoutCopy.unauthenticatedHeader.signUp}
        </Button>

        <Button
          variant="primary"
          onClick={async (): Promise<void> => {
            trackEvent(trackingDetails.loginToConsole);
            await loginRedirectToOsc(pathname);
          }}
        >
          {oscLayoutCopy.unauthenticatedHeader.login}
        </Button>
      </Box>
    </Box>
  );
};

export default UnauthenticatedHeader;
